/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ChangeEvent } from "react";

import { useTranslation } from "react-i18next";

import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import Checkbox from "@/components/CheckBox/Checkbox";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { useSingleInfoLine } from "@/components/PageHeader/actions/dialogs/vehicles/shared/useSingleInfoLine";
import { ReassignVehicleData, useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import { Typography } from "@mui/material";

interface ExchangeImeiDialogProps {
  dialogData: ReassignVehicleData | null;
  isOpen: boolean;
  isChecked: boolean;
  handleCheckbox: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const ExchangeImeiDialog = ({
  dialogData,
  isOpen,
  isChecked,
  handleCheckbox,
  onClose,
  onSubmit
}: ExchangeImeiDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const { company, imei, vehicleNumber } = dialogData || {};

  const { singleInfoLine } = useSingleInfoLine();

  return (
    <Dialog
      title={t("Dialog##edit vehicle imei in use accessible##title")}
      description={t(
        "Dialog##edit vehicle imei in use accessible##description"
      )}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={onClose}
      submit={onSubmit}
      width={toMd ? undefined : DialogWidth.MinWidth}
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={false}
          isreadyToConfirm
          confirmationButtonText={t("Button##continue")}
          confirmationButtonTestId="confirm-accessible-imei-button"
          cancelButtonText={t("Button##back")}
          cancelButtonTestId="close-confirm-accessible-imei"
        />
      }
    >
      <BannerMessage
        data-testid="activate-vehicle-banner"
        status={BANNER_MESSAGE_STATUSES.Info}
        css={css({ marginBottom: "8px" })}
        description={
          <>
            <Typography
              variant="body3Medium"
              component="p"
              sx={{ marginBottom: "4px" }}
            >
              {t("Dialog##add vehicle imei in use accessible##vehicle imei", {
                imeiValue: imei
              })}
            </Typography>
            {singleInfoLine(t("Dialog##vrn"), vehicleNumber)}
            {singleInfoLine(t("Dialog##company"), company)}
          </>
        }
      />

      <Checkbox
        id="exchange-imei-overwrite-checkbox"
        label={t("Dialog##overwrite imei checkbox##title")}
        info={t("Dialog##overwrite imei checkbox##description", {
          vehicleNumber
        })}
        onChange={handleCheckbox}
        checked={isChecked}
        disabled={false}
      />
    </Dialog>
  );
};

export default ExchangeImeiDialog;
